import * as s from "../TableItemStyled";
import { useEffect, useMemo, useState } from "react";
import {
  BasePagination,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { DashboardChartDetailTable } from "components/atoms/table/DashboardChartDetailTable";
import { changeSortOrderAntd } from "components/atoms/table/AntdTable";
import { TableColumnsType } from "antd";
import {
  GetWidgetLoginHistoryResponse,
  LoginHistoryData,
} from "api/interfaces/widgetInterface.interface";
import { getWidgetLoginHistory } from "api/widgetAPI";
import { FaRegUser } from "react-icons/fa";
import { getAccountTimeFormat } from "utils/timeUtil";
import { commonOrgColumn } from "pages/dashboard/dashboard/WidgetContainer";
import { Row } from "pages/users/UsersStyled";
import { ColumnType } from "antd/es/table/interface";
import { Layout } from "react-grid-layout";
import TableItemHeader from "../TableItemHeader";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useIsMobile } from "components/MediaQuery";
import { onSortAntd } from "components/atoms/table/AntdTable";
import { useInfiniteQuery } from "react-query";
import { commonPaginationPerPage, TableItemName } from "utils/WidgetUtil";
import { checkEmpty } from "utils/functions";
interface Props {
  layout: Layout;
  chartLayout: Layout[];
  onDeleteWidget: (key: TableItemName) => void;
  onLayoutChange: (layout: Layout[]) => void;
}
export default function LoginHistory(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const isMobile = useIsMobile();

  const [queryInfo, setQueryInfo] = useState<BasePagination>({
    ...baseQueryInfo,
    pageLimit: commonPaginationPerPage,
    sortType: "loginAt",
    sortDirection: SORT_DIRECTION.DESC,
  });

  const onHeaderCell = (column: ColumnType<LoginHistoryData>) => {
    return {
      onClickCapture: (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (column.key) {
          setQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };

  const columns: TableColumnsType<LoginHistoryData> = [
    commonOrgColumn(selectedAccount),

    {
      title: "User",
      key: "userName",
      dataIndex: "userName",
      render: (value, row) => {
        let isAdmin = row.userGroups?.includes("Administrator");
        return (
          <Row isAdmin={isAdmin}>
            <div>
              <FaRegUser size={14} />
            </div>
            {row.isActivate ? value : checkEmpty(value) ? "Invited User" : value}
          </Row>
        );
      },
      sorter: true,
      showSorterTooltip: false,
      onHeaderCell: onHeaderCell,
      sortOrder: changeSortOrderAntd(queryInfo, "userName"),
      defaultSortOrder: "ascend",
    },
    {
      title: "Group",
      key: "userGroups",
      dataIndex: "userGroups",
      render: (value) => (value ? value.join(",") : ""),
      hidden: isMobile,
      width: selectedAccount.accountLevel === LEVEL_TYPE.EU ? "40%" : "20%",
    },

    {
      title: "Time",
      key: "loginAt",
      dataIndex: "loginAt",
      render: (value) => (
        <s.DateWrapper>
          {getAccountTimeFormat(value, selectedAccount, true)
            .split(" ")
            .map((date, index) => (
              <span key={index}>{date}</span>
            ))}
        </s.DateWrapper>
      ),
      width: "35%",
      sorter: true,
      onHeaderCell: onHeaderCell,
      sortOrder: changeSortOrderAntd(queryInfo, "loginAt"),
      showSorterTooltip: false,
      defaultSortOrder: "descend",
    },
  ];

  const { pageNum, ...loginHistoryQueryKey } = useQueryKey(queryInfo);

  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    isLoading,
    isFetching,
  } = useInfiniteQuery(
    ["getWidgetLoginHistory", loginHistoryQueryKey, selectedAccount.accountId],
    ({ pageParam = 0 }) =>
      getWidgetLoginHistory({
        accountId: selectedAccount.accountId,
        payload: {
          ...queryInfo,
          pageNum: pageNum === 0 ? 0 : pageParam,
        },
      }),
    {
      getNextPageParam: (
        lastPage: GetWidgetLoginHistoryResponse | undefined
      ) => {
        if (!lastPage || !lastPage.page) {
          return undefined;
        }
        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;
        return (pageNum + 1) * pageLimit < total ? nextPage : undefined;
      },
      refetchOnWindowFocus: false,
      retry: 0,
      onSuccess: (res: any) => {
        const last = res.pages[res.pages.length - 1];
        if (last) {
          setQueryInfo((info) => {
            return {
              ...info,
              ...last.page,
              pageNum: last.page.pageNum + 1,
            };
          });
        }
      },
    }
  );
  const lists = useMemo(() => {
    if (
      pageNum === 0 &&
      (isLoading || isFetching) &&
      data?.pages?.length === 0
    ) {
      return [];
    }
    return (data?.pages || [])
      .flatMap((page: GetWidgetLoginHistoryResponse) => page.result ?? [])
      .map((item: LoginHistoryData, index: number) => ({
        ...item,
        key: index,
      }));
  }, [data]);

  useEffect(() => {
    if (props.layout.h >= 2) {
      setQueryInfo((info) => {
        return {
          ...info,
          pageLimit: commonPaginationPerPage * props.layout.h,
        };
      });
    } else {
      setQueryInfo((info) => {
        return {
          ...info,
          pageLimit: commonPaginationPerPage,
        };
      });
    }
  }, [props.layout.h]);

  return (
    <>
      <TableItemHeader
        layout={props.layout}
        chartLayout={props.chartLayout}
        onDeleteWidget={props.onDeleteWidget}
        onLayoutChange={props.onLayoutChange}
        isError={isError}
      />
      <s.Contents className="drag-cancel-content">
        <s.ContentWrapper>
          <s.ContentInner>
            <DashboardChartDetailTable
              data={lists}
              loadMore={() => fetchNextPage()}
              isLoading={isInitialLoading || isFetchingNextPage}
              hasMore={hasNextPage && !isFetchingNextPage && !isInitialLoading}
              columns={columns}
            />
          </s.ContentInner>
        </s.ContentWrapper>
      </s.Contents>
    </>
  );
}
